/**
 * WASSER E-commerce Platform - Shared AdaptiveRoute
 *
 * Single place for "adaptive" routing decisions.
 *
 * IMPORTANT:
 * - This component does NOT define layouts by itself.
 * - Client/Admin must pass the Layout components explicitly.
 * - Optional protection is handled via the provided ProtectedRoute component.
 *
 * This keeps shared clean: no client-only UI (Header/Footer/etc) inside shared.
 */
import React from 'react';

/**
 * @typedef {Object} AdaptiveRouteProps
 * @property {React.ReactNode} children
 * @property {(user: any) => 'b2b' | 'b2c'} getUserType
 * @property {any} user
 * @property {React.ComponentType<{ children: React.ReactNode }>} B2CLayout
 * @property {React.ComponentType<{ children: React.ReactNode }>} B2BLayout
 * @property {React.ComponentType<any>} ProtectedRoute
 * @property {boolean} [requireAuth=false]
 * @property {boolean} [requireB2B=false]
 * @property {boolean} [requireAdmin=false]
 * @property {'b2c'|'b2b'} [forceLayout]
 */

/**
 * @param {AdaptiveRouteProps} props
 */
const AdaptiveRoute = ({
  children,
  getUserType,
  user,
  B2CLayout,
  B2BLayout,
  ProtectedRoute,
  requireAuth = false,
  requireB2B = false,
  requireAdmin = false,
  forceLayout,
}) => {
  const resolvedType =
    forceLayout ||
    (typeof getUserType === 'function' ? getUserType(user) : 'b2c');
  const LayoutComponent = resolvedType === 'b2b' ? B2BLayout : B2CLayout;

  const content = <LayoutComponent>{children}</LayoutComponent>;

  if (requireAuth || requireB2B || requireAdmin) {
    return (
      <ProtectedRoute
        requireAuth={requireAuth}
        requireB2B={requireB2B}
        requireAdmin={requireAdmin}
      >
        {content}
      </ProtectedRoute>
    );
  }

  return content;
};

export default AdaptiveRoute;
